import { useMemo } from 'react'
import { useFilterParams } from '~/context/filters'
import { useSyncToUrlState } from '~/hooks/useUrlState'
import { useDateFormatters } from '~/design_system'
import { NamedQueryFilter } from '~/custom_reports/types'

const cleanValue = (value: string) => {
  return value?.split(',').map(item => {
    const validItem = parseInt(item, 10)
    return Number.isNaN(validItem) ? item : validItem
  })
}

const formatValue = (
  value: string,
  type: string,
  formatDate: (input: Date | string | null | undefined, opts?: { timeZone?: string }) => string | null
) => (type === 'date_range' ? formatDate(new Date(value)) : cleanValue(value))

type DashboardFilterOptions = {
  [key: string]: {
    type: string
    operator?: string
  }
}

/**
 * @param {Object} options object to specify custom operator types, format type..etc
 */
export const useDashboardFilters = (options: DashboardFilterOptions = {}): NamedQueryFilter[] => {
  const filterParams = useFilterParams()
  const { date: formatDate } = useDateFormatters().toISO

  useSyncToUrlState(filterParams)

  return useMemo(
    /*
        eslint-disable-next-line react-hooks/preserve-manual-memoization --

        ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
        Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
        There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
        couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

        Issue: `formatDate` is used inside the callback but only [filterParams, options] are in deps.
        Possible fix approach:
          // Option 1: Add `formatDate` to deps:
          return useMemo(() => Object.keys(filterParams)..., [filterParams, options, formatDate])

          // Option 2: Remove useMemo entirely - the memoization is not needed (verify performance is acceptable):
          return Object.keys(filterParams)
            .filter((id: string) => Boolean(filterParams[id]))
            .map((id: string) => ({
              id,
              value: formatValue(filterParams[id], options[id]?.type, formatDate),
              operator: options[id]?.operator || 'any_of'
            }))
    */
    () =>
      Object.keys(filterParams)
        // Filter out undefined params, sending them breaks reporting apis
        .filter((id: string) => Boolean(filterParams[id]))
        .map((id: string) => ({
          id,
          value: formatValue(filterParams[id], options[id]?.type, formatDate),
          operator: options[id]?.operator || 'any_of'
        })),
    [filterParams, options]
  )
}
